import { ModifyPatientBody } from 'utils/types/apiBodyTypes';
import {
	CustomerInfo,
	ExpandedPatient,
	PatientAlert,
	PatientAlertCategory,
	PatientRecordVisit,
	PetGroupedSearch,
	RDVM,
} from '../utils/dataTypes';
import { pimsApi } from './pimsApi';

export interface PatientNote {
	id: number;
	pet_id: string;
	note: string;
	created_at: number;
	created_by: number;
	file_link?: string;
	file_name?: string;
}

export interface CreatePatientNote {
	note: string;
	pet_id: string;
	file_obj: File | null;
    file_name: string | null;
}

export interface UpdatePatientNote {
	id: number;
	note: string;
	pet_id: string;
	file_link?: string;
    file_obj: File | null;
    file_name: string | null;
}

// Define a service using a base URL and expected endpoints
export const patientApi = pimsApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * GET
		 */
		getPatientById: builder.query<ExpandedPatient, string>({
			query: (petId) => `pet/${petId}`,
			providesTags: (result, error, id) => [{ type: 'Patient', id }],
			transformResponse: (response: { data: ExpandedPatient }) =>
				response.data,
		}),
		getVisitsByPatientId: builder.query<PatientRecordVisit[], string>({
			query: (patient_id) => `pet/${patient_id}/visits`,
			providesTags: (result, error, id) => [{ type: 'Visit', id: 'LIST' }],
			transformResponse: (response: { data: PatientRecordVisit[] }) => {
				return response.data;
			},
		}),
		getCustomerByPetId: builder.query<CustomerInfo[], string>({
			query: (petId) => `pet/${petId}/customers`,
			providesTags: (result, error, id) => {
				return [
					{ type: 'Patient', id },
					...(result?.map((owner) => ({
						type: 'Customer' as 'Customer',
						id: owner.customer_id,
					})) ?? []),
				];
			}, //tags are THIS patient ID, and each Customer ID
			transformResponse: (response: { data: CustomerInfo[] }) =>
				response.data,
		}),
		getRdvmsById: builder.query<RDVM[], string>({
			query: (petId) => `pet/${petId}/rdvms`,
			providesTags: (result, error, id) => [{ type: 'Patient', id }],
			transformResponse: (response: { data: RDVM[] }) => response.data,
		}),
		getPatientAlertCategories: builder.query<PatientAlertCategory[], any>({
            query: () => `pet-alert-categories`,
            transformResponse: (response: { data: PatientAlertCategory[] }) => {
                return response.data;
            },
        }),
		getPatientAlertsById: builder.query<PatientAlert[], string>({
			query: (petId) => `pet/${petId}/alerts`,
			providesTags: (result, error, id) => [{ type: 'Patient', id }],
			transformResponse: (response: { data: PatientAlert[] }) =>
				response.data,
		}),
		patientSearch: builder.query<PetGroupedSearch[], { search_term: string }>({
			query: (params) => {
				return {
					url: 'pet_relationship/search',
					params: params
				}
			},
			providesTags: () => [{ type: 'Patient' }, { type: 'Customer' }],
			transformResponse: (response: { data: PetGroupedSearch[] }) => response.data,
		}),

		/**
		 * MODIFY/MUTATE/PUT/STUFF
		 */
		modifyPatient: builder.mutation<ExpandedPatient, { body: ModifyPatientBody }>(
			{
				query({ body }) {
					const { pet_id, ...modifiedBody } = body;
					return {
						url: `pet/${pet_id}`,
						method: 'PUT',
						body: modifiedBody,
					};
				},
				invalidatesTags: (result, error) => [
					{ type: 'Patient', id: result?.pet_id },
				],
				onQueryStarted(
					{ body, ...patch },
					{ dispatch, queryFulfilled },
				) {
					const { pet_id } = body;
					const patientPatchResult = dispatch(
						patientApi.util.updateQueryData(
							'getPatientById',
							pet_id,
							(draft) => {
								Object.assign(draft, body);
							},
						),
					);
					queryFulfilled.catch(patientPatchResult.undo);
				},
			},
		),
		markPatientDeceased: builder.mutation<ExpandedPatient, { deceased_at: number, pet_id: string }>(
			{
				query({ deceased_at, pet_id }) {
					return {
						url: `pet/${pet_id}/mark_deceased`,
						method: 'POST',
						body: { deceased_at: deceased_at }
					};
				},
				invalidatesTags: (result, error) => [
					{ type: 'Patient', id: result?.pet_id },
				],
			}
		),
		addCustomerToPet: builder.mutation<
			CustomerInfo,
			{ customerId: string; pet_id: string }
		>({
			query({ customerId, pet_id }) {
				return {
					url: `customer/${customerId}/relationship`,
					method: 'PUT',
					body: { pet_id, is_primary: false }, //TODO for now, `is_primary` is always sent as false,
				};
			},
			transformResponse: (response: { data: CustomerInfo }) => {
				return response.data;
			},
			invalidatesTags: (result, error, { customerId, pet_id }) => [
				{ type: 'Patient', id: pet_id }, //Invalidate this patient record
				{ type: 'Customer', id: customerId }, //Invalidate this customer record
			],
		}),
		createPatientNote: builder.mutation<void, CreatePatientNote>({
			query: ({ pet_id, ...body }) => {
				if (!!body.file_name && !!body.file_obj) {
					const formData = new FormData();

					formData.append('note', body.note);
					formData.append('file_obj', body.file_obj, body.file_name);

					return {
						headers: {
							'Content-Type': 'multipart/form-data',
						},
						url: `pet/${pet_id}/note`,
						method: 'POST',
						body: formData,
					};
				}

				return {
					url: `pet/${pet_id}/note`,
					method: 'POST',
					body: { note: body.note, file_obj: null },
				};
			},
			invalidatesTags: (_result, _error, { pet_id }) => ([{ type: 'PatientNote', id: pet_id }])
		}),
		listPatientNotes: builder.query<PatientNote[], string>({
			query: (pet_id) => `pet/${pet_id}/note`,
			providesTags: (_result, _error, pet_id) => ([{ type: 'PatientNote', id: pet_id }]),
			transformResponse: (response: { data: PatientNote[] }) => response.data,
		}),
		updatePatientNote: builder.mutation<void, UpdatePatientNote>({
			query: ({ id, pet_id, ...body }) => {
				if (!!body.file_name && !!body.file_obj) {
					const formData = new FormData();

					formData.append('note', body.note);
					formData.append('file_obj', body.file_obj, body.file_name);

					return {
						headers: {
							'Content-Type': 'multipart/form-data',
						},
						url: `pet/${pet_id}/note/${id}`,
						method: 'PUT',
						body: formData,
					};
				}

				return {
					url: `pet/${pet_id}/note/${id}`,
					method: 'PUT',
					body: { note: body.note, file_obj: null, file_link: body.file_link },
				};
			},
			invalidatesTags: (_result, _error, { pet_id }) => ([{ type: 'PatientNote', id: pet_id }])
		}),
		deletePatientNote: builder.mutation<void, { pet_id: string; noteId: number; }>({
			query: ({ pet_id, noteId }) => ({
				url: `pet/${pet_id}/note/${noteId}`,
				method: 'DELETE'
			}),
			invalidatesTags: (_result, _error, { pet_id }) => ([{ type: 'PatientNote', id: pet_id }])
		})
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
	useLazyPatientSearchQuery,
	useGetVisitsByPatientIdQuery,
	useGetPatientByIdQuery,
	useGetCustomerByPetIdQuery,
	useGetRdvmsByIdQuery,
	useGetPatientAlertCategoriesQuery,
	useGetPatientAlertsByIdQuery,
	useModifyPatientMutation,
	useMarkPatientDeceasedMutation,
	useAddCustomerToPetMutation,
	useCreatePatientNoteMutation,
	useListPatientNotesQuery,
	useUpdatePatientNoteMutation,
	useDeletePatientNoteMutation,
} = patientApi;
